import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { RhEvent } from '../../../api/models/RhEvent';
import { routes } from '../../../route-list';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { ViewEventScreen } from '../components/ViewEventScreen';

export type ViewEventProps = {
  // Inputs
  /** the event to be displayed */
  event: RhEvent | null;
  /** if the confirmation modal is open */
  isConfirmDeleteModalOpen: boolean;
  /** loading event */
  isLoadingEvent: boolean;
  /** if currently requesting to delete event */
  isDeletingEvent: boolean;
  isStartDisabled: boolean;
  isEditDisabled: boolean;
  isDeleteDisabled: boolean;
  isStartShown: boolean;

  // Inputs
  /** called when the user clicks the back to events button */
  userClickedBack: () => void;
  /** user wants to edit current event */
  userClickedEditEvent: () => void;
  /** user wants to initiate event */
  userClickedStartEvent: () => void;
  /** user wants to delete current event, so confirm first */
  userToggledConfirmDeleteModal: () => void;
  /** user is sure to delete current event */
  userClickedOkToDelete: () => void;
  userClickedEndEvent: () => void;
  userClickedGoToStudy: () => void;
};

export const ViewEventContainer = () => {
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [isDeletingEvent, setisDeletingEvent] = useState(false);

  const request = useRequest();
  const history = useHistory();
  const params = useParams<{ id: string }>();

  // load event on mount
  const { loading: isLoadingEvent, data: event } = useAsyncRequest(
    useCallback(() => {
      return request<RhEvent>(apiConfigs.getEvent(params.id));
    }, [params.id, request])
  );

  // Users will not be able to edit/delete/start events that are currently going on
  const isEventGoingOn = useMemo(
    () =>
      event?.startTime && event?.endTime ? dayjs().isAfter(event.startTime) && dayjs().isBefore(event.endTime) : false,
    [event?.endTime, event?.startTime]
  );

  const hasEventStarted = useMemo(() => (event?.startTime ? dayjs().isAfter(event.startTime) : false), [
    event?.startTime,
  ]);

  return (
    <ViewEventScreen
      event={event}
      isLoadingEvent={isLoadingEvent}
      isConfirmDeleteModalOpen={isConfirmDeleteModalOpen}
      isDeletingEvent={isDeletingEvent}
      isStartShown={!isEventGoingOn}
      isStartDisabled={isLoadingEvent || hasEventStarted}
      isEditDisabled={isLoadingEvent || hasEventStarted}
      isDeleteDisabled={isLoadingEvent || isEventGoingOn}
      userClickedGoToStudy={() => {
        if (event) {
          history.push(routes.BIBLE_STUDIES__VIEW(event.theProgram.id));
        }
      }}
      userClickedBack={() => {
        history.push(routes.EVENTS);
      }}
      userClickedEditEvent={() => {
        if (!hasEventStarted) {
          history.push(routes.EVENTS__EDIT(params.id));
        }
      }}
      userClickedStartEvent={async () => {
        if (!hasEventStarted) {
          try {
            await request<RhEvent>(apiConfigs.startEvent(params.id));
            toast.info('Event started on Risen Hope. Start time updated!');
            window.open(event?.vimeoEventUrl || '', '_blank');
            window.location.reload();
          } catch (e: any) {
            toast.error(`Failed to start event - ${e.message}`);
          }
        }
      }}
      userClickedEndEvent={async () => {
        try {
          await request<RhEvent>(apiConfigs.endEvent(params.id));
          toast.info(`Event ended!`, { closeOnClick: false });
          window.location.reload();
        } catch (e: any) {
          toast.error('Failed to end event');
        }
      }}
      userToggledConfirmDeleteModal={() => {
        if (!isEventGoingOn) {
          setIsConfirmDeleteModalOpen(!isConfirmDeleteModalOpen);
        }
      }}
      userClickedOkToDelete={async () => {
        try {
          setisDeletingEvent(true);
          await request(apiConfigs.deleteEvent(params.id));
          history.push(routes.EVENTS);
          toast.success('Event deleted!');
        } catch (e: any) {
          toast.error(`Failed to delete event - ${e.message}`);
        } finally {
          setisDeletingEvent(false);
        }
      }}
    />
  );
};
